import Pane from 'components/Common/Pane'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import LoyaltyLandingPageBenefitsTableSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageBenefitsTableSection'
import LoyaltyLandingPageHowItWorksSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageHowItWorksSection'
import LoyaltyAccountBenefitsContent from './LoyaltyAccountBenefitsContent'
import LoyaltyLandingPageMovingTiersSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageMovingTiersSection'
import LoyaltyLandingPageFaqSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageFaqSection'
import LoyaltyAccountLandingPageHero from './LoyaltyAccountLandingPageHero'
import LoyaltyInfiniteAccountLandingPageHero from './LoyaltyInfiniteAccountLandingPageHero'
import LoyaltyLandingPageUSPSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageUSPSection'
import LoyaltyUserBronzeBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserBronzeBenefits'
import LoyaltyUserGoldBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserGoldBenefits'
import LoyaltyUserPlatinumBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserPlatinumBenefits'
import LoyaltyUserSilverBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserSilverBenefits'
import LoyaltyAccountCurrentOffersSection from './LoyaltyAccountCurrentOffersSection'
import LoyaltyAccountCurrentOffersCarousel from './LoyaltyAccountCurrentOffersCarousel'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import LoyaltyAccountLandingPageHeroNonMemberRegion from './LoyaltyAccountLandingPageHeroNonMemberRegion'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useOnLoyaltyPurchasePointsSuccess from 'loyalty/hooks/useOnLoyaltyPurchasePointsSuccess'

type LoyaltyAccountTab = 'benefits' | 'offers' | 'about'

function LoyaltyAccountLandingPage() {
  const intl = useIntl()
  const hash = useLocation().hash.replace('#', '')

  const activeTab = useMemo<LoyaltyAccountTab>(() => {
    if (hash.startsWith('benefits')) return 'benefits'
    if (hash.startsWith('offers')) return 'offers'
    if (hash.startsWith('about')) return 'about'
    return 'benefits'
  }, [hash])
  const [tier] = useCurrentLoyaltyTier()

  const onSeeBenefits = useCallback(() => {
    const section = document.getElementById('benefits-your-tier')
    if (!section) return
    // Manual offset: scrollIntoView doesn't reliably honour scroll-margin-top here, so the title
    // ends up behind the sticky nav. Offset by the nav height so the heading stays visible.
    const top = section.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top, behavior: 'smooth' })
  }, [])

  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  useOnLoyaltyPurchasePointsSuccess()
  return <div>
    {isCurrentRegionUserLoyaltyRegion && isInfiniteTier && <LoyaltyInfiniteAccountLandingPageHero />}
    {isCurrentRegionUserLoyaltyRegion && !isInfiniteTier && <LoyaltyAccountLandingPageHero activeTab={activeTab} />}
    {!isCurrentRegionUserLoyaltyRegion && <LoyaltyAccountLandingPageHeroNonMemberRegion />}
    {activeTab === 'offers' && <LoyaltyAccountCurrentOffersSection />}
    <Pane background="light-background">
      {activeTab === 'about' && <LoyaltyLandingPageUSPSection />}
      <div>
        {activeTab === 'about' && <>
          {tier === 'bronze' && <LoyaltyUserBronzeBenefits />}
          {tier === 'silver' && <LoyaltyUserSilverBenefits />}
          {tier === 'gold' && <LoyaltyUserGoldBenefits />}
          {tier === 'platinum' && <LoyaltyUserPlatinumBenefits /> }
        </>}
        {activeTab === 'benefits' && <LoyaltyAccountBenefitsContent />}
      </div>
    </Pane>
    {activeTab !== 'offers' && !isInfiniteTier && <LoyaltyLandingPageMovingTiersSection />}
    {activeTab !== 'offers' && !isInfiniteTier && <LoyaltyLandingPageBenefitsTableSection
      highlightTier={tier}
      ctaMobileOnly
      onJoin={onSeeBenefits}
      ctaText={intl.formatMessage({
        id: 'eHngt9pi2t8CYEb91cvK',
        defaultMessage: 'See your benefits',
        description: 'CTA button text to view loyalty program benefits on the promotional landing page',
      })}
    />}
    {activeTab !== 'offers' && !isInfiniteTier && <LoyaltyLandingPageHowItWorksSection />}
    {(activeTab === 'benefits') && <CSSBreakpoint min="tablet">
      <LoyaltyAccountCurrentOffersCarousel />
    </CSSBreakpoint>}
    {activeTab !== 'offers' && <LoyaltyLandingPageFaqSection slug={isInfiniteTier ? 'societe-infinite-faqs' : 'societe-faqs-member'} />}
  </div>
}

export default LoyaltyAccountLandingPage
